/* ==========================================================================
   Live line subtotal beside the quantity stepper (PDP + quick-buy).
   --------------------------------------------------------------------------
   Shows unit-price x quantity on the right of the quantity row, updating as the
   shopper changes quantity or variant. This is the plain line subtotal (no volume
   discount applied); the "Buy more, save more" bar carries the savings message,
   since that discount is cart-quantity-based and the PDP cannot guarantee it.

   Must be a global asset (not inline): the quick-buy injects its markup via
   innerHTML, where inline <script> never executes. One delegated controller bound
   once therefore also governs quick-buy subtotals.

   Quantity signals: typing fires input/change on the input; the +/- buttons set
   input.value through the input-number setter WITHOUT firing change (see theme.js
   QuantitySelector), so we also watch button clicks and arrow keys.
   ========================================================================== */
(function () {
  'use strict';
  if (window.PLLineSubtotalInit) return;
  window.PLLineSubtotalInit = true;

  function moneyFormatString() {
    return (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '${{amount}}';
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var fmt = format || moneyFormatString();
    var rx = /\{\{\s*(\w+)\s*\}\}/;
    function delim(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var rest = parts[1] ? (decimal + parts[1]) : '';
      return dollars + rest;
    }
    var m = fmt.match(rx);
    var token = m ? m[1] : 'amount';
    var value;
    switch (token) {
      case 'amount_no_decimals': value = delim(cents, 0); break;
      case 'amount_with_comma_separator': value = delim(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delim(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = delim(cents, 2, ' ', '.'); break;
      case 'amount_with_apostrophe_separator': value = delim(cents, 2, "'", '.'); break;
      default: value = delim(cents, 2).replace(/\.00$/, ''); /* PL 2026-10-30: whole amounts show without .00 */
    }
    return fmt.replace(rx, value);
  }

  function moneyText(cents) {
    return formatMoney(cents).replace(/<\/?[^>]+>/g, '');
  }

  // Best applicable Buy More Save More discount for a quantity, read from a
  // "qty:pct,qty:pct" string. Thresholds: qty >= tier qualifies; the highest
  // qualifying discount wins. Returns 0 when none applies or no tiers are set.
  function bestDiscount(tierStr, qty) {
    if (!tierStr) return 0;
    var best = 0;
    tierStr.split(',').forEach(function (pair) {
      var parts = pair.split(':');
      var t = parseInt(parts[0], 10);
      var d = parseInt(parts[1], 10) || 0;
      if (!isNaN(t) && qty >= t && d > best) best = d;
    });
    return best;
  }

  function render(container) {
    if (!container) return;
    var unit = parseInt(container.dataset.unitPrice || '0', 10) || 0;
    var row = container.closest('.product-form__qty-row');
    var wrap = container.closest('.product-form__buy-wrap');
    var input = row ? row.querySelector('input[name="quantity"]') : null;
    var qty = input ? Math.max(1, parseInt(input.value, 10) || 1) : 1;

    var full = unit * qty;
    var pct = 0;
    var total = full;

    // Flat tag-based promo (e.g. "20% Off"). Suppressed when the current variant
    // is already a compare-at sale, so we never discount an already-discounted price.
    var onSale = container.dataset.variantOnSale === '1';
    // Fall Sale depth for the selected variant. In calculated mode variant.price
    // is still FULL - the SupaEasy function applies the markdown at checkout - so
    // the button has to apply it here or it contradicts the price above it.
    // Tier 0 means the exclusion came from compare-at, where the markdown is
    // already inside unit and must not be applied twice.
    var saleTier = parseInt(container.dataset.variantSaleTier || '0', 10) || 0;
    var pro = parseInt(container.dataset.proDiscount || '0', 10) || 0;
    var flat = onSale ? 0 : (parseInt(container.dataset.flatDiscount || '0', 10) || 0);
    // Pro Stylist bonus on Fall Sale lines (SupaEasy PRO_BONUS_TIERS). Rendered
    // by product-form.liquid only for Pro customers on non-Bundle-Deal products.
    // Whitelist matches the function: tiers outside it get no bonus at checkout,
    // so the button must not promise one.
    var PRO_BONUS_TIERS = [20, 25, 30, 40, 50];
    var proBonus = parseFloat(container.dataset.proBonus || '0') || 0; // float: bonus can be fractional (6.25)
    if (pro > 0) {
      // Pro Stylist flat discount. Applied order-level at checkout; mirrored here
      // for display. The slot omits it on bundle deals, which are excluded server-side.
      pct = pro;
      // PL 2026-10-08 audit: floor the discount, not round the price, matching
      // snippets/promo-resolver.liquid (price | times: pct | divided_by: 100).
      total = full - Math.floor(full * pro / 100);
    } else if (flat > 0) {
      pct = flat;
      total = (unit - Math.floor(unit * flat / 100)) * qty; // PL 2026-10-08 audit: Liquid rounding
    } else if (onSale) {
      // Fall Sale variant. The markdown IS the promotion, and BMSM drops these
      // lines at checkout, so no volume tier on top. Apply the sale depth when
      // the price is still full (calculated mode); leave it alone when the
      // markdown is already baked into unit (compare-at, tier 0).
      // A Pro Stylist then gets the bonus ON TOP of the tier price -
      // multiplicative, matching the discount function and the resolver
      // (105.00 -> 84.00 -> 75.60), never additive. pct is recomputed from the
      // final unit price so the save line reads the effective 28/32.5/37/46/55
      // instead of overstating (20 + 10 = 30 would be wrong).
      pct = saleTier;
      if (saleTier > 0) {
        var saleUnit = unit - Math.floor(unit * saleTier / 100); // PL 2026-10-08 audit: Liquid rounding
        if (proBonus > 0 && PRO_BONUS_TIERS.indexOf(saleTier) !== -1) {
          // PL 2026-10-08 audit: Liquid rounding (integer bonus floors the discount,
          // a fractional bonus such as 6.25 rounds the final price).
          saleUnit = proBonus % 1 === 0
            ? saleUnit - Math.floor(saleUnit * proBonus / 100)
            : Math.round(saleUnit - saleUnit * proBonus / 100);
          pct = Math.round((1 - saleUnit / unit) * 100);
        }
        total = saleUnit * qty;
      } else {
        total = full;
      }
    } else {
      // Buy More Save More quantity tiers (fallback when no flat promo applies).
      pct = bestDiscount(container.dataset.bmsmTiers, qty);
      total = pct > 0 ? Math.round(full * (1 - pct / 100)) : full;
    }

    if (!wrap) return;

    // 1) Discounted total on the Add to Cart button. Injected as a trailing
    //    sibling of the loader-button text span, so the theme's variant-change
    //    relabel (which rewrites only that text span) never wipes it.
    var btn = wrap.querySelector('[data-product-add-to-cart-button]');
    if (btn) {
      var totalEl = btn.querySelector('[data-atc-total]');
      if (!totalEl) {
        totalEl = document.createElement('span');
        totalEl.className = 'product-form__atc-total';
        totalEl.setAttribute('data-atc-total', '');
        btn.appendChild(totalEl);
      }
      totalEl.textContent = ' • ' + moneyText(total);
      // Keep the total (and savings) in the accessible name.
      var label = btn.getAttribute('data-button-content') || 'Add to cart';
      var aria = label + ', total ' + moneyText(total);
      if (pct > 0) aria += ', save ' + moneyText(full - total);
      btn.setAttribute('aria-label', aria);
    }

    // 2) Smart volume bar + magenta save line, resolved together so the buy area
    //    shows ONE savings message, not two. When a discount is active on a
    //    Buy-More-Save-More product, the bar itself carries the live savings (and,
    //    for quantity tiers, nudges toward the next tier) and the separate magenta
    //    save line stays hidden. The save line is only used when there is no volume
    //    bar to carry the savings (e.g. a Pro Stylist or flat promo on a product
    //    without the bar).
    var hint = wrap.querySelector('[data-volume-hint]');

    var tiers = [];
    (container.dataset.bmsmTiers || '').split(',').forEach(function (p) {
      var a = p.split(':');
      var t = parseInt(a[0], 10), d = parseInt(a[1], 10) || 0;
      if (!isNaN(t)) tiers.push({ qty: t, pct: d });
    });
    tiers.sort(function (a, b) { return a.qty - b.qty; });

    var hintActive = !!hint && pct > 0;

    if (hint) {
      var textEl = hint.querySelector('.volume-hint__text');
      if (textEl) {
        if (hint.dataset.defaultHtml == null) hint.dataset.defaultHtml = textEl.innerHTML;
        if (hintActive) {
          var saved = moneyText(full - total);
          // Nudge toward the next quantity tier - but only when the saving IS the
          // quantity discount. With a flat promo a tier nudge would be misleading.
          var nextTier = null;
          if (flat === 0 && pro === 0) {
            for (var ti = 0; ti < tiers.length; ti++) {
              if (tiers[ti].qty > qty) { nextTier = tiers[ti]; break; }
            }
          }
          if (nextTier) {
            var add = nextTier.qty - qty;
            textEl.innerHTML = 'Saving <strong>' + saved + '</strong> (' + pct + '% off) · add ' +
              add + (add === 1 ? ' pack' : ' packs') + ' for ' + nextTier.pct + '% off';
          } else {
            textEl.innerHTML = "You're at the best price · saving <strong>" + saved + '</strong> (' + pct + '% off)';
          }
        } else {
          textEl.innerHTML = hint.dataset.defaultHtml;
        }
      }
    }

    // Magenta save line: only when no volume bar is carrying the savings. aria-hidden:
    // the same information is already in the Add to Cart button's accessible name.
    var saveEl = wrap.querySelector('[data-save-line]');
    if (saveEl) {
      // qty 1 would just restate the struck price already shown in the price list;
      // the line only adds information once the saving is multiplied by quantity.
      if (pct > 0 && !hintActive && qty >= 2) {
        saveEl.textContent = 'Save ' + moneyText(full - total) + ' · ' + pct + '% off';
        saveEl.hidden = false;
      } else {
        saveEl.textContent = '';
        saveEl.hidden = true;
      }
    }
  }

  function renderFrom(node) {
    var row = node && node.closest ? node.closest('.product-form__qty-row') : null;
    if (!row) return;
    var c = row.querySelector('[data-line-subtotal]');
    if (c) render(c);
  }

  // Cap the quantity input at the selected variant's available stock. The server
  // renders max for the initial variant only; switching variants must re-cap, so
  // we read a per-variant stock map and apply it on load and on variant change.
  // Variants absent from the map are uncapped (untracked or backorder-allowed).
  function applyStockMax(container, variantId) {
    if (!container || variantId == null) return;
    var row = container.closest('.product-form__qty-row');
    var input = row ? row.querySelector('input[name="quantity"]') : null;
    if (!input) return;
    var map = {};
    try { map = JSON.parse(container.dataset.stockMap || '{}'); } catch (e) { map = {}; }
    var maxQ = map[String(variantId)];
    if (maxQ && maxQ > 0) {
      input.setAttribute('max', maxQ);
      input.setAttribute('data-stock-max', maxQ);
      if ((parseInt(input.value, 10) || 1) > maxQ) input.value = String(maxQ);
    } else {
      input.removeAttribute('max');
      input.removeAttribute('data-stock-max');
    }
  }

  // Typed quantity (input/change), plus keyup to catch arrow-key steps
  function onQty(e) { if (e.target && e.target.name === 'quantity') renderFrom(e.target); }
  document.addEventListener('input', onQty);
  document.addEventListener('change', onQty);
  document.addEventListener('keyup', onQty);

  // +/- buttons set input.value via the input-number setter WITHOUT firing change.
  // The quantity-selector's own click handler runs first (it sits below document in the
  // tree, bubble phase), so by the time this fires the value is already updated.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.product-form__qty .quantity-selector__button');
    if (!btn) return;
    var row = btn.closest('.product-form__qty-row');
    if (!row) return;
    var c = row.querySelector('[data-line-subtotal]');
    if (c) render(c);
  });

  // Swaps the in-slot availability message to match the selected variant, using the
  // server-rendered per-variant map. Replaces the old <product-inventory> custom element.
  function updateAvailability(container, variantId) {
    var el = container.querySelector('[data-availability]');
    var mapEl = container.querySelector('[data-availability-map]');
    if (!el || !mapEl || variantId == null) return;
    var map;
    try { map = JSON.parse(mapEl.textContent); } catch (err) { return; }
    // PL 2026-10-08 perf: deduplicated {m:[html], v:{id:index}}; old flat {id:html} still read
    var html = (map && map.v && map.m) ? map.m[map.v[variantId]] : map[variantId];
    el.innerHTML = (html == null) ? '' : html;
  }

  // PL 2026-10-08 audit: data-pro-discount is rendered for the first variant only
  // (and only when Pro WON for it), so on variant change it must be re-resolved or
  // a Pro Stylist sees a button total that disagrees with the price during a Last
  // Chance / Fall Sale promo. Preferred source: the PDP price list rendered with
  // data-pro-eligible, re-resolved through window.plPromo.resolveFromDom - the same
  // call that drives the displayed price. Fallback (quick-buy price lists carry no
  // resolver attributes): the resolver's own rule - Pro wins only when it beats
  // the campaign, ties go to the campaign. Non-Pro customers have no Pro source,
  // so nothing changes for them.
  function priceListFor(form) {
    var fid = form && form.getAttribute ? form.getAttribute('id') : '';
    if (!fid) return null;
    var metas = document.querySelectorAll('product-meta[form-id]');
    for (var i = 0; i < metas.length; i++) {
      if (metas[i].getAttribute('form-id') !== fid) continue;
      var pl = metas[i].querySelector('[data-product-price-list][data-pro-eligible]');
      if (pl) return pl;
    }
    return null;
  }

  function reresolvePro(c, form, v) {
    // Remember Pro eligibility from the first render before overwriting proDiscount.
    if (c.dataset.plProEligible == null) {
      var s = (window.themeVariables && window.themeVariables.settings) || {};
      var elig = parseInt(c.dataset.proDiscount || '0', 10) || 0;
      if (!elig && (parseFloat(c.dataset.proBonus || '0') || 0) > 0) elig = parseInt(s.stylistDiscount || '0', 10) || 0;
      c.dataset.plProEligible = String(elig);
    }
    var pro = 0;
    var pl = priceListFor(form);
    if (pl && window.plPromo && typeof window.plPromo.resolveFromDom === 'function') {
      var r = window.plPromo.resolveFromDom(pl, v);
      if (r && r.badgeType === 'pro') pro = r.badgePct;
    } else {
      var elig2 = parseInt(c.dataset.plProEligible, 10) || 0;
      if (elig2 > 0) {
        var tier = parseInt(c.dataset.variantSaleTier || '0', 10) || 0;
        var flatPct = parseInt(c.dataset.flatDiscount || '0', 10) || 0;
        if (elig2 > Math.max(tier, flatPct)) pro = elig2;
      }
    }
    if (pro > 0) c.dataset.proDiscount = String(pro);
    else delete c.dataset.proDiscount;
  }

  // Variant changed -> refresh unit price (cents), scoped to the dispatching form
  document.addEventListener('variant:changed', function (e) {
    var form = e.target;
    var v = e.detail && e.detail.variant;
    if (!form || !form.querySelectorAll || !v) return;
    form.querySelectorAll('[data-line-subtotal]').forEach(function (c) {
      if (typeof v.price === 'number') c.dataset.unitPrice = v.price;
      // Exclusion is metafield-driven and metafields are absent from the variant
      // JSON, so consult the server-rendered ID list rather than re-deriving the
      // rule from compare_at_price here.
      // "id:tier" pairs from snippets/discount-exclusion.liquid. One parse gives
      // both the membership test and the depth to apply.
      var saleMap = {};
      (c.dataset.bmsmExcluded || '').split(',').filter(Boolean).forEach(function (entry) {
        var parts = entry.split(':');
        if (parts[0]) saleMap[parts[0]] = parseInt(parts[1], 10) || 0;
      });
      var vid = String(v.id);
      if (Object.prototype.hasOwnProperty.call(saleMap, vid)) {
        c.dataset.variantOnSale = '1';
        c.dataset.variantSaleTier = String(saleMap[vid]);
      } else {
        c.dataset.variantOnSale = '';
        c.dataset.variantSaleTier = '0';
      }
      if (v.id != null) { c.dataset.currentVariant = v.id; applyStockMax(c, v.id); updateAvailability(c, v.id); }
      reresolvePro(c, form, v); // PL 2026-10-08 audit: see reresolvePro
      render(c);
    });
  });

  function initAll() {
    document.querySelectorAll('[data-line-subtotal]').forEach(function (c) {
      applyStockMax(c, c.dataset.currentVariant);
      render(c);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
})();
/* Mini-cart thumbnail warm-up.
 * Folded in here rather than shipped as its own asset: the logic is ~30 lines
 * and pl-subtotal.js already loads unconditionally on every page, so a separate
 * file would have cost a whole request to save nothing.
 *
 * The eager attribute on mini-cart line images only helps items already in the
 * cart. The item being added has no markup until /cart/add.js returns, and the
 * PDP gallery (width=400..800) shares no URL with the drawer thumbnail
 * (width=80..184), so it is always a cold fetch starting exactly when the
 * drawer opens. Here we derive that exact URL on submit and request it in
 * parallel with the add. CANDIDATES must stay in sync with the srcset in
 * sections/mini-cart.liquid. */
(function () {
  'use strict';
  var CANDIDATES = [80, 160, 184];

  function pickWidth() {
    var need = 72 * (window.devicePixelRatio || 1);
    for (var i = 0; i < CANDIDATES.length; i++) if (CANDIDATES[i] >= need) return CANDIDATES[i];
    return CANDIDATES[CANDIDATES.length - 1];
  }

  function sourceImage(form) {
    var scopes = [form.closest('.quick-buy-drawer'), form.closest('.drawer'), form.closest('.product'), document];
    var sels = ['.product__media-item.is-selected img', '.product__media-item.is-initial-selected img',
                '.product__media-list .product__media-item img', '.hc-pdp-media-grid img'];
    for (var s = 0; s < scopes.length; s++) {
      if (!scopes[s]) continue;
      for (var i = 0; i < sels.length; i++) {
        var el = scopes[s].querySelector(sels[i]);
        if (el) return el;
      }
    }
    return null;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!form.matches('form[is="product-form"], form[action*="/cart/add"]')) return;
    try {
      var img = sourceImage(form);
      if (!img) return;
      var src = img.currentSrc || img.src;
      if (!src || !/[?&]width=\d+/.test(src)) return;
      var warmer = new Image();
      if ('fetchPriority' in warmer) warmer.fetchPriority = 'high';
      warmer.decoding = 'async';
      warmer.src = src.replace(/([?&])width=\d+/, '$1width=' + pickWidth());
    } catch (e) { /* never interfere with add-to-cart */ }
  }, true);
})();